import React from 'react';
import { TabsContent } from '../../ui/tabs';
import { Button } from '../../ui/button';
import { Input } from '../../ui/input';
import { Label } from '../../ui/label';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../ui/card';
import { Plus, Trash2, Info } from 'lucide-react';

const PRItemOverviewTab = ({
  formData, masterData, currentItem, activeItemIndex,
  handleItemChange, addItem, removeItem, setActiveItemIndex, variant,
}) => {
  const keyPrefix = variant === 'edit' ? 'pr-edit' : 'pr-new';

  return (
    <TabsContent value="items">
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle>PR Line Items</CardTitle>
              <CardDescription>Item overview and basic information</CardDescription>
            </div>
            <Button type="button" onClick={addItem} size="sm" data-testid="pr-add-item-btn">
              <Plus className="h-4 w-4 mr-1" />
              Add Item
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          {/* Item Selector */}
          <div className="flex items-center gap-2 mb-4 flex-wrap">
            {formData.items.map((item, index) => (
              <Button
                key={item.item_number || `${keyPrefix}-${index}`}
                type="button"
                variant={activeItemIndex === index ? 'default' : 'outline'}
                size="sm"
                onClick={() => setActiveItemIndex(index)}
                className="relative"
                data-testid={`pr-item-pill-${item.item_number}`}
              >
                Item {item.item_number}
                {formData.items.length > 1 && (
                  <Trash2
                    className="h-3 w-3 ml-2 cursor-pointer hover:text-red-600"
                    onClick={(e) => { e.stopPropagation(); removeItem(index); }}
                    data-testid={`pr-item-delete-${index}`}
                  />
                )}
              </Button>
            ))}
          </div>

          {currentItem && (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 border-t pt-4">
              <div className="md:col-span-3">
                <div className="bg-orange-50 p-3 rounded-md flex items-start gap-2">
                  <Info className="h-5 w-5 text-orange-600 flex-shrink-0 mt-0.5" />
                  <p className="text-sm text-orange-800">
                    Editing Item {currentItem.item_number}. Switch tabs to edit other sections (Item Details, Valuation, Account Assignment).
                  </p>
                </div>
              </div>

              <div>
                <Label>Item Number</Label>
                <Input value={currentItem.item_number} readOnly className="bg-gray-100" />
              </div>

              {/* Item Category — Create uses sentinel null/'' logic, Edit uses plain value */}
              <div>
                <Label>Item Category *</Label>
                {variant === 'edit' ? (
                  <select
                    value={currentItem.item_category}
                    onChange={(e) => handleItemChange(activeItemIndex, 'item_category', e.target.value)}
                    className="w-full mt-1 px-3 py-2 border border-gray-300 rounded-md text-sm"
                    required
                  >
                    <option value="">Select Item Category</option>
                    {masterData.itemCategories.map((ic) => (
                      <option key={ic.item_category_id} value={ic.item_category_id}>
                        {ic.item_category_id || '(Standard)'} - {ic.description}
                      </option>
                    ))}
                  </select>
                ) : (
                  <select
                    value={currentItem.item_category === null ? '__SELECT__' : (currentItem.item_category === '' ? '__STANDARD__' : currentItem.item_category)}
                    onChange={(e) => {
                      const val = e.target.value;
                      if (val === '__SELECT__') handleItemChange(activeItemIndex, 'item_category', null);
                      else if (val === '__STANDARD__') handleItemChange(activeItemIndex, 'item_category', '');
                      else handleItemChange(activeItemIndex, 'item_category', val);
                    }}
                    className="w-full mt-1 px-3 py-2 border border-gray-300 rounded-md text-sm"
                    required
                  >
                    <option value="__SELECT__">Select Item Category</option>
                    {masterData.itemCategories.map((ic) => (
                      <option key={ic.item_category_id || '__STANDARD__'} value={ic.item_category_id === '' ? '__STANDARD__' : ic.item_category_id}>
                        {ic.item_category_id || '(Standard)'} - {ic.description}
                      </option>
                    ))}
                  </select>
                )}
              </div>

              <div>
                <Label>Material {['', 'K', 'L', 'U'].includes(currentItem.item_category) ? '*' : ''}</Label>
                <select
                  value={currentItem.material_id}
                  onChange={(e) => handleItemChange(activeItemIndex, 'material_id', e.target.value)}
                  className="w-full mt-1 px-3 py-2 border border-gray-300 rounded-md text-sm"
                  disabled={currentItem.item_category === 'D'}
                  required={['', 'K', 'L', 'U'].includes(currentItem.item_category)}
                >
                  <option value="">Select Material</option>
                  {masterData.materials.map((mat) => (
                    <option key={mat.material_number} value={mat.material_number}>
                      {mat.material_number} - {mat.description}
                    </option>
                  ))}
                </select>
              </div>

              <div className="md:col-span-2">
                <Label>Short Text *</Label>
                <Input
                  value={currentItem.short_text}
                  onChange={(e) => handleItemChange(activeItemIndex, 'short_text', e.target.value)}
                  maxLength={40}
                  placeholder="Item description"
                  required
                />
              </div>

              <div>
                <Label>Material Group</Label>
                <select
                  value={currentItem.material_group}
                  onChange={(e) => handleItemChange(activeItemIndex, 'material_group', e.target.value)}
                  className="w-full mt-1 px-3 py-2 border border-gray-300 rounded-md text-sm"
                >
                  <option value="">Select Material Group</option>
                  {masterData.materialGroups.map((mg) => (
                    <option key={mg.material_group_id} value={mg.material_group_id}>
                      {mg.material_group_id} - {mg.description}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <Label>Quantity *</Label>
                <Input
                  type="number" step="0.001" min="0.001"
                  value={currentItem.quantity}
                  onChange={(e) => handleItemChange(activeItemIndex, 'quantity', e.target.value)}
                  placeholder="0.000" required
                />
              </div>

              <div>
                <Label>Unit of Measure *</Label>
                <Input
                  value={currentItem.unit_of_measure}
                  onChange={(e) => handleItemChange(activeItemIndex, 'unit_of_measure', e.target.value)}
                  maxLength={3} placeholder="EA" required
                />
              </div>

              <div>
                <Label>Delivery Date *</Label>
                <Input
                  type="date"
                  value={currentItem.delivery_date}
                  onChange={(e) => handleItemChange(activeItemIndex, 'delivery_date', e.target.value)}
                  required
                />
              </div>

              <div>
                <Label>Delivery Date Category</Label>
                <select
                  value={currentItem.delivery_date_category}
                  onChange={(e) => handleItemChange(activeItemIndex, 'delivery_date_category', e.target.value)}
                  className="w-full mt-1 px-3 py-2 border border-gray-300 rounded-md text-sm"
                >
                  {masterData.deliveryDateCategories.map((ddc) => (
                    <option key={ddc.id} value={ddc.id}>{ddc.name}</option>
                  ))}
                </select>
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    </TabsContent>
  );
};

export default PRItemOverviewTab;
